import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Zap, Database, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import PMChatBot from '../chatbot/pm_chatbot';

/* ── Feature pills shown above the chatbot ── */
const FEATURES = [
  { icon: <BookOpen size={15} />, label: 'FAA Airplane Flying Handbook' },
  { icon: <Database   size={15} />, label: 'RAG Vector Search' },
  { icon: <Zap        size={15} />, label: 'LLM-Powered Answers' },
  { icon: <MessageCircle size={15} />, label: 'Conversational AI' },
];

const RAGChatbotPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => { setIsVisible(true); }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden"
      onMouseMove={handleMouseMove}
    >
      {/* Mouse-follow glow — same as WelcomePage */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle 800px at ${mousePosition.x}px ${mousePosition.y}px, rgba(139,92,246,0.3), transparent)`
        }}
      />

      {/* Floating orbs */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse pointer-events-none" />
      <div className="absolute top-40 right-20 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-pulse pointer-events-none" />
      <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-violet-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-12">

        {/* ── Back link ── */}
        <div className={`mb-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm transition-colors duration-200"
            style={{ color: '#c4b5fd', textDecoration: 'none' }}
            onMouseOver={e => e.currentTarget.style.color = '#f0abfc'}
            onMouseOut={e  => e.currentTarget.style.color = '#c4b5fd'}
          >
            <ArrowLeft size={16} />
            Back to Projects
          </Link>
        </div>

        {/* ── Heading ── */}
        <div className={`text-center mb-10 transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Icon badge */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl blur-xl opacity-60 animate-pulse" />
              <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 p-4 rounded-2xl shadow-xl">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl font-bold text-white mb-3 leading-tight">
            <span className="block bg-gradient-to-r from-violet-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
              RAG Chatbot
            </span>
            <span className="block text-xl md:text-2xl mt-2" style={{ color: '#e2d9f3', fontWeight: 400 }}>
              Airplane Flying Handbook · Knowledge Base
            </span>
          </h1>

          <p style={{ color: '#c4b5fd', fontSize: '15px', maxWidth: '520px', margin: '0 auto 28px', lineHeight: 1.7 }}>
            Ask anything about aviation — powered by the FAA Airplane Flying Handbook
            using Retrieval-Augmented Generation and LLM inference.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-2">
            {FEATURES.map(({ icon, label }) => (
              <div
                key={label}
                className="inline-flex items-center gap-2"
                style={{
                  padding: '7px 16px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
                  background: 'rgba(255,255,255,0.07)',
                  border: '1.5px solid rgba(255,255,255,0.15)',
                  color: '#ddd6fe', backdropFilter: 'blur(6px)',
                }}
              >
                <span style={{ color: '#c084fc' }}>{icon}</span>
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* ── Workflow diagram ── */}
        <div className={`mb-10 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div style={{
            background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(12px)',
            borderRadius: '20px', border: '1.5px solid rgba(255,255,255,0.12)',
            padding: '24px 28px',
            boxShadow: '0 4px 24px rgba(139,92,246,0.1)',
          }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase',
              color: '#a78bfa', marginBottom: '18px' }}>
              // How it works
            </div>

            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
              {[
                { step: '01', label: 'Your Question',    sub: 'Natural language input',   color: '#c084fc' },
                { step: '02', label: 'Vector Search',    sub: 'FAA Handbook index',        color: '#818cf8' },
                { step: '03', label: 'Context Retrieval', sub: 'Top-k relevant chunks',   color: '#60a5fa' },
                { step: '04', label: 'LLM Response',     sub: 'Grounded answer',           color: '#f472b6' },
              ].map(({ step, label, sub, color }, i, arr) => (
                <React.Fragment key={step}>
                  <div style={{ textAlign: 'center', minWidth: '100px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '12px', margin: '0 auto 8px',
                      background: `${color}20`, border: `1.5px solid ${color}50`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: 800, color,
                    }}>{step}</div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#f1f5f9' }}>{label}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{sub}</div>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '20px', flexShrink: 0 }}>→</div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* ── Chatbot section ── */}
        <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Label above chatbot */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <div style={{
              padding: '6px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #7c3aed, #db2777)',
              boxShadow: '0 4px 12px rgba(124,58,237,0.4)',
            }}>
              <MessageCircle size={14} color="#fff" />
            </div>
            <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: '#ddd6fe' }}>
              Start Chatting
            </span>
            {/* Live indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
              <div style={{
                width: '8px', height: '8px', borderRadius: '50%', background: '#4ade80',
                boxShadow: '0 0 8px rgba(74,222,128,0.8)', animation: 'pulse 2s infinite',
              }} />
              <span style={{ fontSize: '11px', color: '#86efac', fontWeight: 600 }}>Live</span>
            </div>
          </div>

          {/* Card wrapping the chatbot */}
          <div style={{
            background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(16px)',
            borderRadius: '24px', border: '1.5px solid rgba(255,255,255,0.15)',
            padding: '28px',
            boxShadow: '0 8px 40px rgba(139,92,246,0.15), 0 2px 10px rgba(0,0,0,0.2)',
            minHeight: '520px',
            display: 'flex', flexDirection: 'column',
          }}>
            {/* Disclaimer */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              background: 'rgba(192,132,252,0.1)', border: '1px solid rgba(192,132,252,0.25)',
              borderRadius: '12px', padding: '12px 16px', marginBottom: '24px',
            }}>
              <BookOpen size={14} style={{ color: '#c084fc', flexShrink: 0, marginTop: '1px' }} />
              <p style={{ fontSize: '12px', color: '#c4b5fd', margin: 0, lineHeight: 1.6 }}>
                This chatbot is trained exclusively on the{' '}
                <a
                  href="https://www.faa.gov/regulations_policies/handbooks_manuals/aviation/airplane_handbook"
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: '#e9d5ff', fontWeight: 600, textDecoration: 'underline' }}
                >
                  FAA Airplane Flying Handbook
                </a>
                . Answers are grounded in that document only.
              </p>
            </div>

            {/* PMChatBot */}
            <div style={{ flex: 1 }}>
              <PMChatBot />
            </div>
          </div>
        </div>

        {/* ── Footer note ── */}
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#475569', marginTop: '32px', lineHeight: 1.7 }}>
          Powered by RAG · Vector Store:{' '}
          <span style={{ color: '#7c6fa0' }}>aircraft_handbook</span>
          {' '}· Backend hosted on Hugging Face Spaces
        </p>

      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
};

export default RAGChatbotPage;